import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Send, RefreshCw, MessageCircle, ArrowLeft, Inbox as InboxIcon, Paperclip, X as XIcon } from 'lucide-react';
import api from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

const fmt = (s) => { try { return new Date(s).toLocaleString(); } catch { return s; } };

export default function AdminInbox() {
  const { toast } = useToast();
  const [convos, setConvos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null); // conv_key
  const [thread, setThread] = useState(null);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [attachment, setAttachment] = useState(null); // {url, type, name}
  const [uploading, setUploading] = useState(false);
  const bottomRef = useRef(null);

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/admin/crm/upload-media', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setAttachment({ url: r.data.url, type: r.data.type, name: r.data.name });
    } catch (err) { toast({ title: 'Upload failed', description: err?.response?.data?.detail, variant: 'destructive' }); }
    finally { setUploading(false); }
  };

  const loadConvos = async () => {
    try {
      const r = await api.get('/admin/crm/inbox');
      setConvos(r.data?.conversations || []);
    } catch { /* ignore */ } finally { setLoading(false); }
  };

  const openThread = async (key) => {
    setActive(key);
    setThread(null);
    try {
      const r = await api.get(`/admin/crm/inbox/${encodeURIComponent(key)}`);
      setThread(r.data);
      loadConvos(); // refresh unread badges after marking read
    } catch { toast({ title: 'Could not load conversation' }); }
  };

  useEffect(() => { loadConvos(); }, []);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [thread]);

  const sendReply = async () => {
    if ((!reply.trim() && !attachment) || !active) return;
    setSending(true);
    try {
      await api.post(`/admin/crm/inbox/${encodeURIComponent(active)}/reply`, {
        text: reply,
        attachment_url: attachment?.url, attachment_type: attachment?.type, attachment_name: attachment?.name,
      });
      setReply('');
      setAttachment(null);
      await openThread(active);
    } catch (e) { toast({ title: 'Reply failed', description: e?.response?.data?.detail, variant: 'destructive' }); }
    finally { setSending(false); }
  };

  return (
    <div className="p-4 sm:p-8 max-w-6xl mx-auto" data-testid="admin-inbox">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-mahogany/60">CRM</div>
          <h1 className="font-serif-display text-3xl sm:text-4xl text-burgundy flex items-center gap-2">
            <InboxIcon className="w-7 h-7" /> Inbox
          </h1>
          <p className="text-sm text-mahogany/70 mt-1">SMS &amp; WhatsApp replies from your contacts — reply in real time.</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button onClick={loadConvos} data-testid="inbox-refresh" className="pill-btn-outline text-sm inline-flex items-center gap-1.5"><RefreshCw className="w-4 h-4" /> Refresh</button>
          <Link to="/dashboard/admin/customers" className="pill-btn-outline text-sm inline-flex items-center gap-1.5"><ArrowLeft className="w-4 h-4" /> Customers</Link>
        </div>
      </div>

      <div className="grid md:grid-cols-[320px_1fr] gap-4">
        {/* Conversation list */}
        <div className={`rounded-2xl border border-rose/40 bg-white/60 overflow-hidden ${active ? 'hidden md:block' : ''}`} data-testid="inbox-list">
          {loading ? (
            <div className="p-6 text-center text-mahogany/60"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
          ) : convos.length === 0 ? (
            <div className="p-6 text-center text-mahogany/60 text-sm">No conversations yet. Replies from your SMS/WhatsApp contacts will appear here.</div>
          ) : (
            <div className="divide-y divide-rose/30 max-h-[70vh] overflow-y-auto">
              {convos.map((c) => (
                <button key={c.conv_key} onClick={() => openThread(c.conv_key)} data-testid={`inbox-convo-${c.conv_key}`}
                  className={`w-full text-left px-4 py-3 hover:bg-rose-soft/30 transition-colors ${active === c.conv_key ? 'bg-rose-soft/40' : ''}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium text-burgundy truncate">{c.name || c.phone || 'Unknown'}</span>
                    {c.unread > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-burgundy text-cream shrink-0">{c.unread}</span>}
                  </div>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-100 text-green-800 capitalize">{c.channel}</span>
                    <span className="text-xs text-mahogany/70 truncate">{c.last_direction === 'in' ? '' : 'You: '}{c.last_text || '…'}</span>
                  </div>
                  <div className="text-[10px] text-mahogany/50 mt-0.5">{fmt(c.last_at)}</div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Thread */}
        <div className="rounded-2xl border border-rose/40 bg-white/60 flex flex-col min-h-[60vh]" data-testid="inbox-thread">
          {!active ? (
            <div className="flex-1 flex items-center justify-center text-mahogany/50 text-sm p-8">
              <div className="text-center"><MessageCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />Pick a conversation to view the thread.</div>
            </div>
          ) : !thread ? (
            <div className="flex-1 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-mahogany/60" /></div>
          ) : (
            <>
              <div className="px-4 py-3 border-b border-rose/30 flex items-center gap-2">
                <button onClick={() => { setActive(null); setThread(null); }} className="md:hidden text-mahogany/60"><ArrowLeft className="w-4 h-4" /></button>
                <div>
                  <div className="text-sm font-medium text-burgundy">{thread.name || thread.phone}</div>
                  <div className="text-[11px] text-mahogany/60">{thread.phone} · <span className="capitalize">{thread.channel}</span></div>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-2 max-h-[55vh]" data-testid="thread-messages">
                {thread.thread.map((m) => (
                  <div key={m.id} className={`flex ${m.direction === 'out' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm ${m.direction === 'out' ? 'bg-burgundy text-cream rounded-br-sm' : 'bg-rose-soft/60 text-burgundy rounded-bl-sm'}`}>
                      {m.attachment_url && (
                        <a href={m.attachment_url} target="_blank" rel="noreferrer" className="block mb-1" data-testid="thread-attachment">
                          {/\.(mp4|mov|webm)(\?|$)/i.test(m.attachment_url)
                            ? <video src={m.attachment_url} controls className="rounded-lg max-h-48" />
                            : /\.(png|jpe?g|gif|webp)(\?|$)/i.test(m.attachment_url)
                              ? <img src={m.attachment_url} alt="attachment" className="rounded-lg max-h-48 object-cover" />
                              : <span className="underline inline-flex items-center gap-1"><Paperclip className="w-3.5 h-3.5" /> View attachment</span>}
                        </a>
                      )}
                      {m.text && <p className="whitespace-pre-wrap break-words">{m.text}</p>}
                      {!m.text && !m.attachment_url && <span className="italic opacity-70">[no text]</span>}
                      <div className={`text-[10px] mt-1 ${m.direction === 'out' ? 'text-cream/70' : 'text-mahogany/50'}`}>
                        {fmt(m.at)}{m.direction === 'out' && m.status ? ` · ${m.status}` : ''}
                      </div>
                      {m.direction === 'out' && m.status === 'failed' && m.error && (
                        <div className="mt-1 text-[10px] leading-snug bg-red-100 text-red-800 rounded-md px-2 py-1" data-testid={`thread-error-${m.id}`}>
                          {m.error}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                <div ref={bottomRef} />
              </div>
              <div className="p-3 border-t border-rose/30 space-y-2">
                {attachment && (
                  <div className="flex items-center gap-2 text-xs bg-rose-soft/40 rounded-lg px-2.5 py-1.5" data-testid="inbox-attachment-chip">
                    <Paperclip className="w-3.5 h-3.5 text-mahogany/70" />
                    <span className="flex-1 truncate text-mahogany">{attachment.name || attachment.type} · {attachment.type}</span>
                    <button onClick={() => setAttachment(null)} data-testid="inbox-attachment-remove" className="text-mahogany/60 hover:text-burgundy"><XIcon className="w-3.5 h-3.5" /></button>
                  </div>
                )}
                <div className="flex items-end gap-2">
                  <label className="shrink-0 cursor-pointer p-2 rounded-xl border border-rose/50 bg-white hover:bg-rose-soft/40 text-mahogany" data-testid="inbox-attach-btn" title="Attach image, video or PDF">
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
                    <input type="file" accept="image/*,video/*,application/pdf" className="hidden" onChange={onPickFile} data-testid="inbox-file-input" />
                  </label>
                  <textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={2}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
                    placeholder="Type a reply…" data-testid="inbox-reply-input"
                    className="flex-1 bg-white rounded-xl border border-rose/50 px-3 py-2 text-sm text-burgundy focus:outline-none focus:border-mahogany resize-none" />
                  <button onClick={sendReply} disabled={sending || uploading || (!reply.trim() && !attachment)} data-testid="inbox-reply-send"
                    className="pill-btn shrink-0 inline-flex items-center gap-1.5 disabled:opacity-50">
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send
                  </button>
                </div>
              </div>
              <p className="text-[10px] text-mahogany/50 px-3 pb-2">WhatsApp free-form replies work within 24h of the contact's last message; after that, use a template campaign.</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
